/**
 * SEO Manager - Handles loading and applying SEO settings
 */

import { blogService } from './database'

interface SeoSettings {
  [key: string]: {
    title: string;
    description: string;
    keywords: string;
    image?: string;
  };
}

// Default SEO settings
const defaultSeoSettings: SeoSettings = {
  home: {
    title: 'Grad Vikalp - Find Your Dream College | College Guidance Platform',
    description: 'Find your dream college with Grad Vikalp! Get verified student reviews, personalized college recommendations, and connect with real students. Your trusted partner for JEE, NEET, and college admissions guidance.',
    keywords: 'college admission, JEE colleges, NEET colleges, college reviews, personalized college list, engineering colleges, medical colleges, college guidance, student reviews, college comparison, admission counseling',
    image: '/Grad Vikalp (3).png'
  },
  about: {
    title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
    description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections. Discover our story, values, and commitment to student success.',
    keywords: 'about grad vikalp, college guidance platform, education counseling, student success, college selection help, verified college reviews',
    image: '/Grad Vikalp (3).png'
  },
  blog: {
    title: 'College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp',
    description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts. Stay updated with latest college admission trends and insights.',
    keywords: 'college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling, education blog, college selection tips',
    image: '/Grad Vikalp (3).png'
  },
  'college-reviews': {
    title: 'College Reviews - Verified Student Reviews | Grad Vikalp',
    description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India. Make informed decisions with verified student experiences and ratings.',
    keywords: 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews, authentic college feedback, college experiences, verified reviews',
    image: '/Grad Vikalp (3).png'
  },
  'neet-college-predictor': {
    title: 'NEET College Predictor - AI-Powered Medical College Recommendations | Grad Vikalp',
    description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis for just ₹299. Instant results with detailed college information.',
    keywords: 'NEET college predictor, NEET college recommendations, medical college list, NEET admission probability, rank-based medical colleges, AI medical college recommendations, NEET college selection tool',
    image: '/Grad Vikalp (3).png'
  },
  'josaa-personalized-list': {
    title: 'JoSAA College Predictor - IIT, NIT, IIIT Admission Predictions | Grad Vikalp',
    description: 'Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions based on your JEE Main and JEE Advanced ranks. AI-powered recommendations with admission probability analysis.',
    keywords: 'JoSAA college predictor, IIT admission predictor, NIT college predictor, JEE college recommendations, JEE Main predictions, JEE Advanced predictions',
    image: '/Grad Vikalp (3).png'
  },
  contact: {
    title: 'Contact Grad Vikalp - Get College Guidance & Support',
    description: 'Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form. We\'re here to help with your college selection journey.',
    keywords: 'contact grad vikalp, college guidance support, admission counseling, education help, college selection assistance, student support',
    image: '/Grad Vikalp (3).png'
  }
};

/**
 * Get SEO settings for a specific page
 * @param pagePath The path of the page (e.g., 'about', 'blog')
 * @returns SEO settings for the page
 */
export const getPageSeoSettings = async (pagePath: string) => {
  // Normalize path
  const normalizedPath = pagePath.startsWith('/') ? pagePath.substring(1) : pagePath;
  const path = normalizedPath || 'home';
  
  try {
    // Import blogService dynamically to avoid circular imports
    const { blogService } = await import('./database');
    
    // Try to get settings from database first
    const dbSettings = await blogService.getPageSEO(path);
    if (dbSettings) {
      console.log(`Loaded SEO from database for ${path}:`, dbSettings);
      return {
        title: dbSettings.meta_title,
        description: dbSettings.meta_description,
        keywords: dbSettings.meta_keywords,
        image: dbSettings.og_image,
        canonical_url: dbSettings.canonical_url,
        og_title: dbSettings.og_title,
        og_description: dbSettings.og_description,
        twitter_title: dbSettings.twitter_title,
        twitter_description: dbSettings.twitter_description,
        schema_type: dbSettings.schema_type
      };
    }
    
    console.log(`No database SEO found for ${path}, using defaults`);
    
    // Use defaults
    return defaultSeoSettings[path] || defaultSeoSettings.home;
  } catch (error) {
    console.error('Error loading SEO settings:', error);
    return defaultSeoSettings[path] || defaultSeoSettings.home;
  }
};

/**
 * Get SEO settings for a blog post
 * @param post The blog post object
 * @returns SEO settings for the blog post
 */
export const getBlogPostSeoSettings = (post: any) => {
  if (!post) return defaultSeoSettings.blog;
  
  // Create comprehensive meta description
  const metaDescription = post.excerpt || 
    `Read "${post.title}" - A comprehensive ${post.category.toLowerCase()} guide by ${post.author}. ` +
    `Get expert insights, tips, and advice for college admissions and academic success. ${post.read_time}.`;
  
  // Enhanced keywords with long-tail variations
  const enhancedKeywords = [
    ...(post.tags || []),
    post.category,
    `${post.category} guidance`,
    `${post.category} tips`,
    `${post.category} advice`,
    'college guidance',
    'admission tips',
    'education blog',
    'grad vikalp',
    post.author,
    `${post.category} 2025`,
    `college admission ${post.category.toLowerCase()}`,
    `${post.category.toLowerCase()} preparation`,
    `${post.category.toLowerCase()} strategy`
  ].filter(Boolean).join(', ');
  
  return {
    title: `${post.title} | ${post.category} Guide 2025 | Expert Tips by ${post.author} | Grad Vikalp`,
    description: metaDescription,
    keywords: enhancedKeywords,
    image: post.image_url || defaultSeoSettings.blog.image
  };
};

/**
 * Get SEO settings for a college page
 * @param college The college object
 * @returns SEO settings for the college page
 */
export const getCollegeSeoSettings = (college: any) => {
  if (!college) return defaultSeoSettings['college-reviews'];
  
  // Create comprehensive meta description
  const metaDescription = 
    `Complete guide to ${college.name} - ${college.type} ${college.category} college in ${college.location}, ${college.state}. ` +
    `📋 Detailed information: Fees (${college.fees}), Cutoffs (${college.cutoff}), Courses, Student Reviews, Facilities. ` +
    `⭐ Rating: ${college.rating}/5 | 🏛️ Established: ${college.established} | 🎓 ${college.category} Programs | ` +
    `Get admission guidance, compare with other colleges, and read authentic student experiences.`;
  
  // Enhanced keywords with location and program variations
  const enhancedKeywords = [
    college.name,
    `${college.name} reviews`,
    `${college.name} admission`,
    `${college.name} fees`,
    `${college.name} cutoff`,
    `${college.name} courses`,
    `${college.name} placement`,
    `${college.category} college ${college.location}`,
    `${college.category} colleges in ${college.state}`,
    `${college.location} colleges`,
    `${college.state} colleges`,
    `${college.type} college`,
    `${college.category} education`,
    `college fees ${college.fees}`,
    'admission cutoff',
    'student reviews',
    'college comparison',
    'college facilities',
    'placement records',
    `${college.category} admission 2025`,
    `best ${college.category} colleges`,
    `top colleges in ${college.state}`,
    'college guidance',
    'grad vikalp'
  ].filter(Boolean).join(', ');
  
  return {
    title: `${college.name} - Complete College Guide, Reviews & Admission Details 2025 | Grad Vikalp`,
    description: metaDescription,
    keywords: enhancedKeywords,
    image: college.image_url || defaultSeoSettings.home.image
  };
};

/**
 * Get SEO settings for blog listing by category
 * @param category The blog category
 * @returns SEO settings for the category page
 */
export const getBlogCategorySeoSettings = (category: string) => {
  if (!category || category === 'all') return defaultSeoSettings.blog;
  
  const categoryMap: {[key: string]: {title: string, description: string, keywords: string}} = {
    'Guidance': {
      title: 'College Guidance Articles & Resources | Grad Vikalp Blog',
      description: 'Expert college guidance articles to help you navigate the admission process. Get tips on college selection, application strategies, and career planning.',
      keywords: 'college guidance, admission guidance, career counseling, college selection help, application tips, education guidance'
    },
    'JEE': {
      title: 'JEE Preparation & College Admission Guide | Grad Vikalp Blog',
      description: 'Complete guide for JEE aspirants - preparation tips, exam strategies, college selection based on JEE rank, and admission process for engineering colleges.',
      keywords: 'JEE preparation, JEE Main, JEE Advanced, engineering admission, IIT admission, NIT colleges, JEE rank predictor, JEE cutoffs'
    },
    'NEET': {
      title: 'NEET Preparation & Medical College Guide | Grad Vikalp Blog',
      description: 'Comprehensive NEET preparation resources, medical college admission guides, and expert tips for MBBS aspirants. Find the best medical colleges based on your NEET rank.',
      keywords: 'NEET preparation, NEET UG, medical college admission, MBBS admission, AIIMS, JIPMER, medical college cutoffs, NEET rank predictor'
    },
    'Career': {
      title: 'Career Guidance & Planning Resources | Grad Vikalp Blog',
      description: 'Expert career guidance articles to help students make informed decisions about their future. Explore career options, industry insights, and job prospects.',
      keywords: 'career guidance, career planning, job prospects, career options, professional development, industry insights, career counseling'
    },
    'Admissions': {
      title: 'College Admission Guides & Resources | Grad Vikalp Blog',
      description: 'Step-by-step college admission guides, application tips, document checklists, and important dates. Navigate the admission process with confidence.',
      keywords: 'college admissions, admission process, application tips, admission documents, college application, admission deadlines, admission counseling'
    },
    'Scholarships': {
      title: 'Scholarship Guides & Financial Aid Resources | Grad Vikalp Blog',
      description: 'Comprehensive guides to scholarships, financial aid, education loans, and fee structures for various colleges. Find ways to fund your education.',
      keywords: 'scholarships, financial aid, education loans, merit scholarships, need-based scholarships, college funding, tuition fee, scholarship application'
    }
  };
  
  return categoryMap[category] || {
    title: `${category} Articles & Resources | Grad Vikalp Blog`,
    description: `Read expert articles and resources about ${category} from Grad Vikalp's education blog. Get insights, tips, and guidance for your college journey.`,
    keywords: `${category}, ${category.toLowerCase()} guidance, ${category.toLowerCase()} tips, college education, student resources, grad vikalp blog`
  };
};

/**
 * Get SEO settings for college listings by state or stream
 * @param type 'state' or 'stream'
 * @param value The state or stream name
 * @returns SEO settings for the listing page
 */
export const getCollegeListingSeoSettings = (type: 'state' | 'stream', value: string) => {
  if (!value || value === 'all') return defaultSeoSettings['college-reviews'];
  
  // Format the value for display (convert slug to title case)
  const formattedValue = value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  if (type === 'state') {
    return {
      title: `Top Colleges in ${formattedValue} - Ratings & Reviews | Grad Vikalp`,
      description: `Discover the best colleges in ${formattedValue}. Compare ratings, fees, facilities, and read authentic student reviews. Find engineering, medical, and other top colleges in ${formattedValue}.`,
      keywords: `colleges in ${formattedValue.toLowerCase()}, ${formattedValue.toLowerCase()} colleges, top colleges in ${formattedValue.toLowerCase()}, engineering colleges in ${formattedValue.toLowerCase()}, medical colleges in ${formattedValue.toLowerCase()}, college reviews, college ratings`
    };
  } else {
    return {
      title: `Best ${formattedValue} Colleges in India - Reviews & Ratings | Grad Vikalp`,
      description: `Find the best ${formattedValue} colleges in India. Compare fees, placements, facilities, and read verified student reviews. Make informed decisions about your ${formattedValue.toLowerCase()} education.`,
      keywords: `${formattedValue.toLowerCase()} colleges, top ${formattedValue.toLowerCase()} colleges, best ${formattedValue.toLowerCase()} institutes, ${formattedValue.toLowerCase()} education, ${formattedValue.toLowerCase()} courses, college reviews, college ratings`
    };
  }
};

export default {
  getPageSeoSettings,
  getBlogPostSeoSettings,
  getCollegeSeoSettings,
  getBlogCategorySeoSettings,
  getCollegeListingSeoSettings
};